import React, { useState } from 'react';
import { Check, X, Loader, AlertCircle, Save, Send } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GenericWebhookWizard = ({ onClose, onSave, platform }) => {
  const [config, setConfig] = useState({
    webhook_url: '',
    channel: '',
    username: 'SYLINK Security',
    icon_emoji: ':shield:',
    notify_on: ['critical', 'high'],
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const platforms = {
    teams: {
      name: 'Microsoft Teams',
      placeholder: 'https://outlook.office.com/webhook/...',
      icon: '💼',
      description: 'Receive security alerts directly in your Teams channels',
      helpUrl: 'https://docs.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
    },
    discord: {
      name: 'Discord',
      placeholder: 'https://discord.com/api/webhooks/...',
      icon: '🎮',
      description: 'Real-time notifications on your Discord server',
      helpUrl: 'https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks',
    },
    mattermost: {
      name: 'Mattermost',
      placeholder: 'https://mattermost.exemple.com/hooks/...',
      icon: '💬',
      description: 'Integration with your self-hosted Mattermost instance',
      helpUrl: 'https://developers.mattermost.com/integrate/incoming-webhooks/',
    },
    slack: {
      name: 'Slack',
      placeholder: 'https://hooks.slack.com/services/...',
      icon: '💬',
      description: 'Slack notifications for your security team',
      helpUrl: 'https://api.slack.com/messaging/webhooks',
    },
  };

  const currentPlatform = platforms[platform] || platforms.teams;
  const integrationId = platform;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // First save config to backend
      const saveResponse = await fetch(`${API_BASE}/api/v1/system/integrations/${integrationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: false
        }),
      });

      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }

      // Then test via backend API
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/${integrationId}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTestResult({
          success: true,
          message: result.message || `Test message sent to ${currentPlatform.name}!`
        });
      } else {
        setTestResult({ success: false, message: result.error || 'Send failed' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/${integrationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: true
        }),
      });

      if (response.ok) {
        onSave(platform, config);
        onClose();
      } else {
        const error = await response.json();
        setTestResult({ success: false, message: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleSeverity = (severity) => {
    setConfig(prev => ({
      ...prev,
      notify_on: prev.notify_on.includes(severity)
        ? prev.notify_on.filter(s => s !== severity)
        : [...prev.notify_on, severity]
    }));
  };

  const severityOptions = [
    { id: 'critical', label: 'Critical', color: 'bg-red-500' },
    { id: 'high', label: 'High', color: 'bg-orange-500' },
    { id: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { id: 'low', label: 'Low', color: 'bg-blue-500' },
    { id: 'info', label: 'Info', color: 'bg-gray-500' },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{currentPlatform.icon}</span>
            <div>
              <h2 className="text-2xl font-bold text-brand-green">{currentPlatform.name}</h2>
              <p className="text-sm text-gray-400">{currentPlatform.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Webhook URL */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Webhook URL *
            </label>
            <input
              type="url"
              value={config.webhook_url}
              onChange={(e) => setConfig({ ...config, webhook_url: e.target.value })}
              placeholder={currentPlatform.placeholder}
              className="input-field w-full"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              <a
                href={currentPlatform.helpUrl}
                target="_blank"
                rel="noreferrer"
                className="text-brand-green hover:underline"
              >
                How to create a {currentPlatform.name} webhook?
              </a>
            </p>
          </div>

          {/* Channel (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Channel (optionnel)
            </label>
            <input
              type="text"
              value={config.channel}
              onChange={(e) => setConfig({ ...config, channel: e.target.value })}
              placeholder="#security-alerts"
              className="input-field w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use the default webhook channel
            </p>
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Nom d'affichage
            </label>
            <input
              type="text"
              value={config.username}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
              placeholder="SYLINK Security"
              className="input-field w-full"
            />
          </div>

          {/* Severity Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-3">
              Notify for severities
            </label>
            <div className="flex flex-wrap gap-2">
              {severityOptions.map((severity) => (
                <button
                  key={severity.id}
                  onClick={() => toggleSeverity(severity.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    config.notify_on.includes(severity.id)
                      ? `${severity.color} text-white`
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {severity.label}
                </button>
              ))}
            </div>
          </div>

          {/* Test Button */}
          <div className="pt-4 border-t border-gray-800">
            <button
              onClick={handleTest}
              disabled={testing || !config.webhook_url}
              className="btn-green w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {testing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Envoyer un message test</span>
                </>
              )}
            </button>

            {testResult && (
              <div className={`mt-4 p-4 rounded-lg border flex items-start gap-3 ${
                testResult.success
                  ? 'bg-brand-green/10 border-brand-green/50'
                  : 'bg-red-500/10 border-red-500/50'
              }`}>
                {testResult.success ? (
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div>
                  <div className={`font-medium ${testResult.success ? 'text-brand-green' : 'text-red-400'}`}>
                    {testResult.success ? 'Success' : 'Failure'}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{testResult.message}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!testResult?.success || saving}
            className="btn-green disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Sauvegarder & Activer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenericWebhookWizard;
